import { Color } from '@persona-id/design-tokens';
import { CircleAlertIcon, TransferOutIcon } from '@persona-id/design-tokens/icons/16px';
import MoreIcon from '@persona-id/design-tokens/icons/MoreIcon';
import { Helmet } from 'react-helmet-async';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';

import CasesSummary from 'dashboard/components/CasesSummary';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import Permissioned from 'dashboard/components/Permissioned';
import ReactSelect from 'dashboard/components/ReactSelect';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import {
  Backdrop,
  ColumnContainer,
  FullScreenHeaderContainer,
  MainColumn,
  RightColumn,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { Case } from 'dashboard/models/case';
import { Environment } from 'dashboard/models/environment';
import { SAR, SARData } from 'dashboard/models/sar';
import { SARConfiguration } from 'dashboard/models/sar-configuration';
import { User } from 'dashboard/models/user';
import { Z_INDICES } from 'dashboard/styles/variables';
import { SARErrorMessagesBanner } from 'dashboard/views/DashboardSARShowView/components/SARErrorMessagesBanner';
import { SARInfoCard } from 'dashboard/views/DashboardSARShowView/components/SARInfoCard';
import { SARInstructionsCard } from 'dashboard/views/DashboardSARShowView/components/SARInstructionsCard';
import { JSONFormSAR } from 'dashboard/views/DashboardSARShowView/FormSAR';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import useConfirmation from 'lib/hooks/useConfirmation';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';
import { permissionErrorString } from 'lib/permissions-errors';
import { useFormSecurely } from 'lib/security/reactHookForm';

const PERSONA_ENVIRONMENT_TO_FINCEN_ENVIRONMENT = {
  'environment/production': 'production',
  'environment/sandbox': 'sandbox',
};

export interface DashboardSARShowViewProps {
  sar?: SAR;
  errorMessages: Record<string, string>;
  additionalValidationErrors: string[]; // additional validation errors come from XML schema validation in the backend
  relatedCases: Case[];
  users: User[];
  currentEnvironment: Environment;
  sarConfiguration?: SARConfiguration;
  isLoading: boolean;
  nullStateMessage: string;
  onSARDataUpdate: (sarData: SARData) => void;
  handleArchiveSAR: () => Promise<void>;
  handleAutofillSAR: () => Promise<void>;
  handleCloneSAR: ({ caseTokens }: { caseTokens: string[] }) => Promise<void>;
  handleExportSAR: () => Promise<void>;
  handleFileSAR: () => Promise<void>;
  handlePrintSAR: () => Promise<void>;
  handleValidateSAR: () => Promise<void>;
  handleExportFincenPDF: () => Promise<void>;
}

const DisabledTooltipIconContainer = styled('div')`
  cursor: pointer;
  pointer-events: auto;

  svg path {
    fill: ${Color.CoreSlate700};
  }
  svg {
    vertical-align: middle;
  }
`;

const StyledMainColumn = styled(MainColumn)`
  width: 75%;
`;

const StyledRightColumn = styled(RightColumn)`
  width: 25%;
  min-width: 350px;
  padding-right: 0px;
`;

const DashboardSARsView = (props: DashboardSARShowViewProps) => {
  const {
    isLoading,
    nullStateMessage,
    sar,
    relatedCases,
    users,
    currentEnvironment,
    sarConfiguration,
    errorMessages,
    additionalValidationErrors,
    onSARDataUpdate,
    handleArchiveSAR,
    handleAutofillSAR,
    handleCloneSAR,
    handleExportSAR,
    handleFileSAR,
    handlePrintSAR,
    handleValidateSAR,
    handleExportFincenPDF,
  } = props;

  const hasSARCreatePermission = usePermission(Permission.SARCreate);
  const hasSARWritePermission = usePermission(Permission.SARWrite);
  const hasSARFilePermission = usePermission(Permission.SARFile);
  const hasSARExportPermission = usePermission(Permission.SARExport);

  const [
    showConfirmManualFilingModal,
    { prompt: promptManualFiling, confirm: confirmManualFiling, cancel: cancelManualFiling },
  ] = useConfirmation();
  const [
    showConfirmEFilingModal,
    { prompt: promptEFiling, confirm: confirmEFiling, cancel: cancelEFiling },
  ] = useConfirmation();
  const [
    showArchiveModal,
    { prompt: promptArchive, confirm: confirmArchive, cancel: cancelArchive },
  ] = useConfirmation();
  const [
    showExportPDFModal,
    { prompt: promptExportPDF, confirm: confirmExportPDF, cancel: cancelExportPDF },
  ] = useConfirmation();
  const [
    showExportFincenPDFModal,
    {
      prompt: promptExportFincenPDF,
      confirm: confirmExportFincenPDF,
      cancel: cancelExportFincenPDF,
    },
  ] = useConfirmation();
  const [showCloneModal, { enable: openCloneModal, disable: hideCloneModal }] = useToggle(false);

  if (isLoading) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    );
  }

  if (sar == null) {
    return (
      <Backdrop>
        <NullState message={nullStateMessage} backRoute='/dashboard/sars' />
      </Backdrop>
    );
  }

  const isReadOnly = !!sar.attributes['filed-at'] || !!sar.attributes['archived-at'];

  const credentialsForCurrentEnvironment = sarConfiguration?.attributes[
    'jurisdiction-credentials'
  ].find((cred) => {
    return cred.environment === PERSONA_ENVIRONMENT_TO_FINCEN_ENVIRONMENT[currentEnvironment.type];
  });

  const exportItems = [
    {
      id: 'sar-header__print-sar',
      name: 'sar-header__print-sar',
      label: 'PDF',
      variant: 'basic' as const,
      onClick: () => {
        promptExportPDF(handlePrintSAR);
      },
    },
    {
      id: 'sar-header__export-fincen-pdf',
      name: 'sar-header__export-fincen-pdf',
      label: 'FinCEN Form 111',
      variant: 'basic' as const,
      onClick: () => {
        promptExportFincenPDF(handleExportFincenPDF);
      },
    },
  ].filter((item) => ('hidden' in item ? !item.hidden : true));

  const moreItems = [
    {
      id: 'sar-header__export-as-xml',
      name: 'sar-header__export-as-xml',
      label: 'Export SAR for manual filing',
      variant: 'basic' as const,
      onClick: () => {
        promptManualFiling(handleExportSAR);
      },
      disabled: !hasSARExportPermission || isReadOnly,
      disabledTooltip: permissionErrorString(Permission.SARExport),
    },
    {
      id: 'sar-header__autofill-sar',
      name: 'sar-header__autofill-sar',
      label: 'Autofill SAR with attached objects',
      variant: 'basic' as const,
      onClick: handleAutofillSAR,
      disabled: !hasSARWritePermission || isReadOnly,
      disabledTooltip: [
        !hasSARWritePermission ? permissionErrorString(Permission.SARWrite) : '',
        isReadOnly ? 'Cannot autofill a readonly SAR' : '',
      ].join('\n'),
    },
    {
      id: 'sar-header__clone-sar',
      name: 'sar-header__clone-sar',
      label: 'Clone',
      variant: 'basic' as const,
      onClick: openCloneModal,
      disabled: !hasSARCreatePermission,
      disabledTooltip: permissionErrorString(Permission.SARCreate),
    },
    {
      id: 'sar-header__archive-sar',
      name: 'sar-header__archive-sar',
      label: 'Archive',
      variant: 'danger' as const,
      onClick: () => {
        promptArchive(handleArchiveSAR);
      },
      disabled: !hasSARWritePermission || isReadOnly,
      disabledTooltip: [
        !hasSARWritePermission ? permissionErrorString(Permission.SARWrite) : '',
        isReadOnly ? 'Cannot archive a readonly SAR' : '',
      ],
    },
  ].filter((item) => ('hidden' in item ? !item.hidden : true));

  return (
    <>
      <Helmet title={`${sar.attributes.name} - SARs`} />
      <InactiveEnvironmentBanner />
      <JSONFormSAR
        sar={sar}
        additionalValidationErrors={additionalValidationErrors}
        scope={sar.id}
        onSubmit={onSARDataUpdate}
        onAsyncValidate={handleValidateSAR}
      >
        {({ renderedForm, isDirty, submitForm, validateForm }) => {
          return (
            <>
              <FullScreenHeaderContainer>
                <NavBack to='/dashboard/sars'>All SARs</NavBack>
                <HStack css={{ justifySelf: 'end' }}>
                  <Tooltip
                    trigger={
                      <Button
                        id='sar-header__button--validate'
                        variant='basic'
                        disabled={isReadOnly || isDirty}
                        onClick={validateForm}
                      >
                        Validate
                      </Button>
                    }
                  >
                    {isDirty && <p>Please save the form before validating</p>}
                    {isReadOnly && <p>Cannot validate a readonly SAR</p>}
                  </Tooltip>
                  <Tooltip
                    trigger={
                      <Button
                        id='sar-header__button--save'
                        variant='basic'
                        onClick={submitForm}
                        disabled={!hasSARWritePermission || isReadOnly || !isDirty}
                      >
                        Save
                      </Button>
                    }
                  >
                    {!hasSARWritePermission && (
                      <p>Your role does not have permission to edit SARs</p>
                    )}
                    {isReadOnly && <p>Cannot save readonly SAR</p>}
                    {!isDirty && <p>There are no changes to save</p>}
                  </Tooltip>
                  <Tooltip
                    trigger={
                      <Button
                        id='sar-header__button--send-to-fincen'
                        variant='primary'
                        onClick={() => promptEFiling(handleFileSAR)}
                        disabled={
                          !hasSARFilePermission ||
                          !credentialsForCurrentEnvironment ||
                          isReadOnly ||
                          isDirty
                        }
                      >
                        Send to FinCEN
                      </Button>
                    }
                  >
                    {!hasSARFilePermission && (
                      <p>Your role does not have permission to file SARs</p>
                    )}
                    {!credentialsForCurrentEnvironment && (
                      <p>No credentials for this environment</p>
                    )}
                    {isReadOnly && <p>Can not file a readonly SAR</p>}
                  </Tooltip>
                  <Permissioned permission={Permission.SARExport}>
                    <DropdownMenu
                      trigger={
                        <Button
                          id='cases__export'
                          variant='basic'
                          data-test='cases__export'
                          prefixIcon={<TransferOutIcon />}
                        >
                          Export
                        </Button>
                      }
                      align='end'
                    >
                      {exportItems.map((item) => (
                        <DropdownMenu.Item
                          id={item.id}
                          variant={item.variant}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            item.onClick();
                          }}
                          key={item.id}
                        >
                          {item.label}
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu>
                  </Permissioned>
                  <DropdownMenu
                    trigger={
                      <Button
                        id='sar-header__button--more'
                        tabIndex={0}
                        variant='basic'
                        width='auto'
                        prefixIcon={<MoreIcon />}
                      >
                        More
                      </Button>
                    }
                  >
                    {moreItems.map((item) => (
                      <DropdownMenu.Item
                        id={item.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          item.onClick();
                        }}
                        key={item.id}
                        disabled={item.disabled}
                        variant={item.variant}
                        suffixIcon={
                          item.disabled &&
                          item.disabledTooltip && (
                            <Tooltip
                              hideArrow
                              side='bottom'
                              trigger={
                                <DisabledTooltipIconContainer>
                                  <CircleAlertIcon size={16} />
                                </DisabledTooltipIconContainer>
                              }
                              zIndex={Z_INDICES.toast + Z_INDICES.tooltip}
                            >
                              <Paragraph color='primary' style={{ width: '350px' }}>
                                {item.disabledTooltip}
                              </Paragraph>
                            </Tooltip>
                          )
                        }
                      >
                        {item.label}
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu>
                </HStack>
              </FullScreenHeaderContainer>
              <SARErrorMessagesBanner
                status={sar.attributes.status}
                errorMessages={errorMessages}
              />
              <Backdrop>
                <ColumnContainer>
                  <StyledMainColumn>{renderedForm}</StyledMainColumn>
                  <StyledRightColumn>
                    <SARInfoCard sar={sar} />
                    <SARInstructionsCard />
                    <CasesSummary
                      cases={relatedCases}
                      users={users}
                      nullStateText='There are no cases associated with this SAR.'
                    />
                  </StyledRightColumn>
                </ColumnContainer>
              </Backdrop>
            </>
          );
        }}
      </JSONFormSAR>
      <ModalWrapper isOpen={showExportPDFModal} handleCloseModal={cancelExportPDF}>
        <ConfirmationModal
          cancelAction={cancelExportPDF}
          confirmAction={confirmExportPDF}
          header='Export as PDF?'
          confirmActionText='Export'
          confirmButtonType='primary'
        >
          This action will export SAR in PDF format. The generated PDF will be sent to your email.
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={showExportFincenPDFModal} handleCloseModal={cancelExportFincenPDF}>
        <ConfirmationModal
          cancelAction={cancelExportFincenPDF}
          confirmAction={confirmExportFincenPDF}
          header='Export as FinCEN Form 111?'
          confirmActionText='Export'
          confirmButtonType='primary'
        >
          This action will export SAR in FinCEN Form 111 format. The generated PDF will be sent to
          your email.
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={showConfirmManualFilingModal} handleCloseModal={cancelManualFiling}>
        <ConfirmationModal
          cancelAction={cancelManualFiling}
          confirmAction={confirmManualFiling}
          header='Export SAR for manual filing?'
          confirmActionText='Sign and export'
        >
          This action will sign and lock the SAR preventing any further edits. This action can not
          be undone.
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={showConfirmEFilingModal} handleCloseModal={cancelEFiling}>
        <ConfirmationModal
          cancelAction={cancelEFiling}
          confirmAction={confirmEFiling}
          header='Submit to FinCEN?'
          confirmActionText='Sign and submit'
        >
          This action will sign and lock the SAR preventing any further edits. This action can not
          be undone.
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={showArchiveModal} handleCloseModal={handleArchiveSAR}>
        <ConfirmationModal
          cancelAction={cancelArchive}
          confirmAction={confirmArchive}
          header='Archive SAR?'
          confirmActionText='Archive'
        >
          This action will permanently archive the SAR preventing any further edits. This action can
          not be undone.
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={showCloneModal} handleCloseModal={hideCloneModal}>
        <ModalContainer>
          <ModalHeader onClose={hideCloneModal}>Clone SAR</ModalHeader>

          <ModalBody>
            <FormCloneSAR
              relatedCases={relatedCases}
              onCancel={hideCloneModal}
              onSubmit={handleCloneSAR}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

interface FormCloneSARProps {
  relatedCases: Case[];
  onCancel: () => void;
  onSubmit: ({ caseTokens }: { caseTokens: string[] }) => void;
}

const FormCloneSAR = ({ relatedCases, onCancel, onSubmit }: FormCloneSARProps) => {
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
  } = useFormSecurely({
    defaultValues: {
      caseTokens: relatedCases.map((kase) => kase.id),
    },
    mode: 'onChange',
  });

  return (
    <form>
      <Paragraph>
        The cloned SAR will be automatically associated with the selected cases.
      </Paragraph>
      <Spacer height={2} />
      <Label id='form-clone-sar__case-token-label' size='md' weight='bold'>
        Related cases
      </Label>
      <Controller
        name='caseTokens'
        control={control}
        render={({ field }) => (
          <ReactSelect
            {...field}
            id='form-clone-sar__select-case-token-id'
            aria-labelledby='form-clone-sar__case-token-label'
            isMulti
            disabled={isSubmitting}
            options={relatedCases.map((kase) => ({
              label: kase.attributes.name,
              value: kase.id,
            }))}
            portal
          />
        )}
      />

      <Spacer height={3} />

      <HStack equal>
        <Button id='clone-sar-form__close' onClick={onCancel} variant='basic'>
          Cancel
        </Button>
        <Button
          id='clone-sar-form__submit'
          disabled={!isValid}
          onClick={async () => {
            await handleSubmit(onSubmit)();
            // close modal afterwards
            onCancel();
          }}
        >
          Clone
        </Button>
      </HStack>
    </form>
  );
};

export default DashboardSARsView;
